/** SD-2489: the HTML body of an action card, as the plain text the card carries. */

/** Ordered so `Math.max` picks the stronger of two adjacent breaks. */
const LINE_BREAK = 1;
const PARAGRAPH_BREAK = 2;

type Piece = string | number;

const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DIV',
  'DL',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'UL',
]);

const LINE_TAGS = new Set(['LI', 'DD', 'DT', 'TR']);

const DROPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'NOSCRIPT']);

/** U+00A0 too: `&nbsp;` must not reach the body as a stray glyph or skew a column. */
function collapseWhitespace(value: string): string {
  return value.replace(/[\s\u00a0]+/g, ' ');
}

/** Tables render whole and skip their subtree: shape matters, text order does not. */
function walk(node: Node, out: Piece[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push(collapseWhitespace(node.nodeValue ?? ''));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as Element;
  const tag = element.tagName;

  if (DROPPED_TAGS.has(tag)) return;

  if (tag === 'BR') {
    out.push(LINE_BREAK);
    return;
  }

  if (tag === 'TABLE') {
    const rendered = renderTable(element);
    if (rendered) out.push(PARAGRAPH_BREAK, rendered, PARAGRAPH_BREAK);
    return;
  }

  const boundary = BLOCK_TAGS.has(tag)
    ? PARAGRAPH_BREAK
    : LINE_TAGS.has(tag)
      ? LINE_BREAK
      : null;

  if (boundary) out.push(boundary);
  if (tag === 'LI') out.push('- ');

  for (const child of Array.from(element.childNodes)) walk(child, out);

  if (boundary) out.push(boundary);
}

/** Breaks emit lazily, which drops the ones every wrapper element contributes. */
function joinPieces(pieces: Piece[]): string {
  const parts: string[] = [];
  let pending = 0;

  for (const piece of pieces) {
    if (typeof piece === 'number') {
      pending = Math.max(pending, piece);
      continue;
    }
    // Whitespace against a break is source indentation; without one it separates words.
    if (piece === '' || (pending > 0 && piece.trim() === '')) continue;

    if (parts.length > 0 && pending > 0) {
      parts.push(pending === PARAGRAPH_BREAK ? '\n\n' : '\n');
    }
    pending = 0;
    parts.push(piece);
  }

  return parts.join('');
}

/** Via `walk`, not `textContent`, so `A<br>B` separates instead of running together. */
function cellText(cell: Element): string {
  const pieces: Piece[] = [];
  for (const child of Array.from(cell.childNodes)) walk(child, pieces);
  const flattened = pieces
    .map(piece => (typeof piece === 'number' ? ' ' : piece))
    .join('');
  return collapseWhitespace(flattened).trim();
}

/**
 * Markdown, so `MarkdownRendererService` builds a real table for the card preview and
 * the clipboard. Cell padding is for the note and task textareas, which show text raw.
 * Short rows are padded out rather than dropped, which would lose a deal.
 */
function renderTable(table: Element): string {
  const rows = Array.from(table.querySelectorAll('tr'))
    .map(tr => Array.from(tr.querySelectorAll('th, td')).map(cellText))
    .filter(cells => cells.length > 0);
  if (rows.length === 0) return '';

  const columnCount = Math.max(...rows.map(cells => cells.length));
  const padded = rows.map(cells => [
    ...cells,
    ...Array<string>(columnCount - cells.length).fill(''),
  ]);

  const widths = Array.from({ length: columnCount }, (_, column) =>
    Math.max(...padded.map(cells => (cells[column] ?? '').length))
  );

  const renderRow = (cells: string[]) =>
    `| ${cells
      .map((cell, column) => cell.padEnd(widths[column] ?? 0))
      .join(' | ')} |`;

  const lines = padded.map(renderRow);

  // No delimiter row, no table — so a table without `<th>` gets its first row promoted.
  if (lines.length > 1) {
    lines.splice(
      1,
      0,
      `| ${widths.map(width => '-'.repeat(width)).join(' | ')} |`
    );
  }

  return lines.join('\n');
}

/**
 * Tag-free content is markdown often enough, and the walk would collapse its newlines.
 *
 * SD-2821: only a real tag counts. Markdown legitimately carries `<` followed by a letter — an
 * autolink (`<https://…>`), an address (`<ellie@synply.io>`), a placeholder — and treating that as
 * HTML parsed the whole body as markup, losing its line breaks and dropping the bracketed text as
 * an unknown element. Same criterion as the backend's `EmailBodyFormatter`.
 */
const HTML_TAG =
  /<\/?(?:p|div|br|hr|ul|ol|li|h[1-6]|table|thead|tbody|tr|td|th|blockquote|pre|span|strong|em|b|i|u|a|html|head|body|style|script)(?=[\s/>])[^>]*>/i;

export function htmlToPlainText(html: string): string {
  if (!html || typeof html !== 'string') return '';
  if (!HTML_TAG.test(html)) return html.trim();

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const pieces: Piece[] = [];
  for (const child of Array.from(parsed.body.childNodes)) walk(child, pieces);

  return joinPieces(pieces).trim();
}
